import React from 'react';
import { isNil } from 'ramda';
import classnames from 'classnames';
import pluralize from 'pluralize';
import Icon from '@ant-design/icons';
import { ReactComponent as Intersect } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_intersect.svg';

import { ImageAssetType } from '@skytvnz/sky-app-store/lib/types/enums/ImageAssetType';
import { Show } from '@skytvnz/sky-app-store/lib/types/graph-ql';

import { utils } from '@/Store';
import { formatGenres, getGenres } from '@/Utils/Genres';
import { PageType } from '@/Components/HeroBanner/HeroTypes';
import { formatRating } from '@/Utils/Rating';

import SlotProgress from '@/Components/SlotProgress/SlotProgress';
import Availability from '@/Pages/Detail/Seasons/Episodes/Availability';
import useMedia from '@/Hooks/useMedia';
import synopsisStyles from '@/Components/Synopsis/styles.module.scss';
import { PackageDetails } from '@/Hooks/PUJ/usePackageDetail';
import Synopsis from '@/Components/Synopsis';
import usePujFlag from '@/Hooks/usePujFlag';
import { CustomerPackageOffer } from '@/lib/graph-ql';
import { useContentSubscriptionCheck } from '@/Hooks/useSubscription';
import HeroBox from './HeroBox';
import ControllingChannel from './ControllingChannel';
import LiveTag from '../LiveTag';
import HeroButtons from './HeroButtons';
import DetailsHero from './DetailsHero';
import styles from './styles.module.scss';
import { PinValidationModalProps } from '../Modal/usePinValidationModal';

const NUMBER_OF_GENRES_TO_DISPLAY = 2;

interface Props {
  show: Show;
  image: string;
  imageType: ImageAssetType;
  pageType: PageType;
  className?: string;
  origin?: string;
  onMyListToggle?: any;
  packageDetails?: PackageDetails;
  offerDetails?: CustomerPackageOffer;
  openPinModal?: (props: PinValidationModalProps) => void;
  indexOnPage: number;
}

const ShowHeroBanner: React.FC<Props> = props => {
  const {
    show,
    pageType,
    imageType,
    image,
    className,
    onMyListToggle,
    packageDetails,
    origin,
    offerDetails,
    openPinModal,
    indexOnPage = 1,
  } = props;
  const { isPackageDetailPage, discountPrice } = packageDetails || {};
  const isHome = pageType === PageType.Home;

  const { hasVodOrLinearPkgUpgrade } = useContentSubscriptionCheck();
  const hasPkgUpgrade = !isHome && (isPackageDetailPage || hasVodOrLinearPkgUpgrade(show));

  const firstAvailableSlot = utils.title.getFirstAvailableSlot(show);
  const isSport = utils.title.isSport(show);
  const shouldHeroDisplaySlotDetail = utils.hero.shouldDisplaySlotDetail(show);
  const isFirstAvailableSlotOnAir = utils.slot.isSlotOnAir(firstAvailableSlot);
  const heroImage = utils.hero.getImage(show, image);
  // Get Synopsis firstly from the first available upcoming slot
  const synopsis = isPackageDetailPage ? offerDetails?.synopsis : utils.hero.getSynopsis(show);

  const { isPUJEnabled } = usePujFlag(true);
  const HeroDetails = () => {
    const { isMediaM } = useMedia();
    const genresList = getGenres(formatGenres(show.allGenres), NUMBER_OF_GENRES_TO_DISPLAY);
    return (
      <>
        <div className={styles.textContentContainer}>
          {isPackageDetailPage && discountPrice && (
            <div className={styles.promoContainer}>
              <div className={styles.hintBox}>
                <span className={styles.discountText}>{discountPrice}</span>
              </div>

              <Icon className={styles.intersectIcon} component={Intersect as React.FC} />
            </div>
          )}
          {!isHome && !isMediaM && <ControllingChannel channels={show.brands} />}
          <h1 data-testid="hero-title">
            {isPackageDetailPage ? `Get ${offerDetails?.title}` : show.title}
          </h1>
          <div className={styles.rowContainer}>
            {genresList && <span data-testid="hero-genres">{genresList}</span>}
            <div className={styles.secondaryAttributes}>
              {!isNil(show.numberOfSeasons) && show.numberOfSeasons > 0 && (
                <span className={styles.delimiter} data-testid="hero-seasons">
                  {pluralize('Season', show.numberOfSeasons, true)}
                </span>
              )}
              {show.year && (
                <span
                  className={classnames({ [styles.delimiter]: show.rating })}
                  data-testid="hero-year"
                >
                  {show.year}
                </span>
              )}
              {show.rating && <span data-testid="hero-rating">{formatRating(show.rating)}</span>}
            </div>
            {isSport && firstAvailableSlot && (
              <span className={styles.sportTitle} data-testid="sport-episode-title">
                {firstAvailableSlot.programme?.title}
              </span>
            )}
            {shouldHeroDisplaySlotDetail && (
              <>
                <div className={styles.upcomingSlot} data-testid="sport-upcoming-slot">
                  <LiveTag
                    className={styles.bigLiveTag}
                    testId="hero-live-tag"
                    slot={firstAvailableSlot}
                  />
                  <Availability
                    id={0}
                    startDate={firstAvailableSlot?.start}
                    channelName={firstAvailableSlot?.channel?.title}
                    className={styles.availability}
                  />
                </div>
              </>
            )}
          </div>
          {isMediaM && (
            <Synopsis
              synopsis={synopsis}
              cast={show.defaultEpisode?.characters}
              className={synopsisStyles.synopsis}
              hasPkgUpgrade={hasPkgUpgrade}
            />
          )}
          {shouldHeroDisplaySlotDetail && isFirstAvailableSlotOnAir && (
            <SlotProgress
              slotStart={firstAvailableSlot?.start}
              slotEnd={firstAvailableSlot?.end}
              channelTitle={firstAvailableSlot?.channel.title}
            />
          )}
          {(!isPUJEnabled || !hasPkgUpgrade) && (
            <HeroButtons
              indexOnPage={indexOnPage}
              brand={show}
              isHome={isHome}
              onMyListToggle={onMyListToggle}
              hasPkgUpgrade={hasPkgUpgrade}
              origin={origin}
              openPinModal={openPinModal}
              packageDetails={packageDetails}
            />
          )}
          {isPUJEnabled && isMediaM && (
            <DetailsHero
              brand={show}
              isHome={isHome}
              packageDetails={packageDetails}
              onMyListToggle={onMyListToggle}
              hasPkgUpgrade={hasPkgUpgrade}
              origin={origin}
              openPinModal={openPinModal}
            />
          )}
        </div>
        {(isHome || isMediaM) && <ControllingChannel isHome={isHome} channels={show.brands} />}
      </>
    );
  };
  return (
    <HeroBox
      imageTitle={show.title}
      image={heroImage}
      imageType={imageType}
      className={className}
      testId="hero-banner-show"
    >
      <HeroDetails />
    </HeroBox>
  );
};

export default ShowHeroBanner;
